import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { AppointmentForPatientResponse } from '@/types/scheduling-api';

/**
 * Patient chart Appointments tab — Chart › Appointments (PROD-734 chart copy).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:903-922`
 * `patientAppointments`):
 *
 *   POST /api/Appointment/ForPatient
 *        body: { patientId, pageNumber, pageSize, includePrevious,
 *                LoggedInUserTimezone }
 *        → AppointmentListResponse
 *
 * `includePrevious: true` makes the backend return BOTH past and future
 * occurrences in one page so the tab can split them client-side into the
 * Future / Past sections (Flutter `appts_tab.dart:97-107`). Flutter fetches
 * pageNumber 1 / pageSize 10 — we mirror that exactly (no paging UI on this
 * tab).
 *
 * This is a DIFFERENT endpoint from the patient-edit tab's
 * `/api/Appointment/ByPatientId` facet list (`usePatientAppointments`): the
 * chart copy is a simpler read with no filters. Curl-verified 2026-06-04.
 *
 * All auth headers (organizationId / practiceId / userId / roleId /
 * userTimeZone / ipAddress / appType) are injected by `apiClient`.
 */

const PAGE_SIZE = 10;

export function usePatientAppointmentsForChart(
  patientId: string | null | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['patient-appointments-for-chart', patientId],
    queryFn: ({ signal }) =>
      post<AppointmentForPatientResponse>(
        '/api/Appointment/ForPatient',
        {
          patientId: patientId ?? '',
          pageNumber: 1,
          pageSize: PAGE_SIZE,
          includePrevious: true,
          LoggedInUserTimezone:
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { signal },
      ),
    enabled: !!patientId && enabled,
    staleTime: 30_000,
  });
}
